import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { LogOut, User as UserIcon, BookOpen } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export function UserMenu() {
  const { profile, signOut } = useAuth();
  const nav = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  if (!profile) return null;

  const initials = (profile.full_name ?? profile.email ?? "?").slice(0, 1).toUpperCase();
  const isStaff = profile.role === "staff";
  const dashboardPath = isStaff ? "/app/staff/jobs-board" : "/app/customer/dashboard";

  // Determine primary text and subtext based on user role and data
  let primaryText: string;
  let subtext: string;

  if (isStaff) {
    primaryText = profile.full_name || "Staff user";
    subtext = profile.email || "";
  } else {
    // Customer
    const hasTeams = (profile as any).teams_enabled === true;
    const companyName = (profile as any).company_name;

    if (hasTeams && companyName) {
      primaryText = companyName;
      subtext = profile.email || "";
    } else {
      primaryText = profile.full_name || "";
      subtext = profile.email || "";
    }
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        className="flex items-center gap-2 rounded-full px-3 py-1.5"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-hero text-primary-foreground text-sm font-bold">
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt="Avatar"
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
        <div className="hidden flex-col items-start sm:flex">
          <span className="text-sm font-medium leading-tight">{primaryText}</span>
          {subtext && (
            <span className="text-xs text-muted-foreground leading-tight">{subtext}</span>
          )}
        </div>
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-2xl border border-border bg-card p-2 shadow-soft">
          <Link
            to={dashboardPath}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-muted"
            onClick={() => setIsOpen(false)}
          >
            <UserIcon className="h-4 w-4" /> Go to dashboard
          </Link>
          <div className="my-1 h-px bg-border" />
          <Link
            to="/app/settings"
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-muted"
            onClick={() => setIsOpen(false)}
          >
            Settings
          </Link>
          <div className="my-1 h-px bg-border" />
          <div className="px-3 py-2">
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Theme</div>
            <ThemeToggle className="w-full justify-between" />
          </div>
          <Link
            to="/app/help"
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-muted"
            onClick={() => setIsOpen(false)}
          >
            <BookOpen className="h-4 w-4" /> Help & docs
          </Link>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => {
              signOut();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-muted text-left"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
